import React, { FunctionComponent, useCallback, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Box, Checkbox, FormControlLabel, IconButton, InputAdornment } from "@mui/material";
import { SendOutlined } from "@mui/icons-material";

import { QUERY_KEYS } from "consts";
import { CommentDTO, createComment as createCommentApi, createSiteComment as createSiteCommentApi, UpdateCommentDTO } from "api";
import { useAmplitude, useNotify } from "hooks";
import { MaybeNull } from "types";
import { TextField } from "components";

import { UserSelectPopover } from "./user-select-popover";

export const CommentTextField: FunctionComponent<{
  id: string;
  siteId?: number;
  isEstimate?: boolean;
  comment?: CommentDTO | UpdateCommentDTO;
  onSuccessfulSubmit?: () => void;
}> = ({ id, onSuccessfulSubmit, siteId, isEstimate = false, comment }) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { trackEvent } = useAmplitude();

  const { mutateAsync: createComment, isLoading: isCreating } = useMutation(createCommentApi);
  const { mutateAsync: createSiteComment, isLoading: isCreatingSite } = useMutation(createSiteCommentApi);

  const userIdRef = useRef<MaybeNull<number>>(null);

  const [isTask, setIsTask] = useState<boolean>(false);
  const [value, setValue] = useState<string>("");

  const onUserSelect = useCallback((currentUserId: MaybeNull<number>) => {
    userIdRef.current = currentUserId;
    setIsTask(!!currentUserId);
  }, []);

  const parentId = (comment as CommentDTO)?.id ?? null;
  const billId = (comment as UpdateCommentDTO)?.billId ?? id ?? null;

  const handleComments = () => {
    if (isEstimate) {
      createSiteComment(
        {
          assignedUserId: isTask && userIdRef.current ? userIdRef.current : null,
          estimateId: id,
          parentId,
          siteId,
          text: value
        },
        {
          onSuccess: () => {
            notify.success("Comment was successfully added!");
            if (userIdRef.current) {
              trackEvent("Tagged User", "Document view - Comments");
            } else {
              trackEvent("Left Comment", "Document view - Comments");
            }
            if (onSuccessfulSubmit) {
              onSuccessfulSubmit();
            }
          },
          onError: () => notify.error("Failed to add a comment!"),
          onSettled: () => {
            queryClient.invalidateQueries([QUERY_KEYS.ESTIMATES, id]);
          },
        }
      )
      setValue("");
    } else {
      createComment(
        {
          assignedUserId: isTask && userIdRef.current ? userIdRef.current : null,
          parentId,
          billId,
          text: value,
        },
        {
          onSuccess: () => {
            notify.success("Comment was successfully added!");
            if (userIdRef.current) {
              trackEvent("Tagged User", "Document view - Comments");
            } else {
              trackEvent("Left Comment", "Document view - Comments");
            }
            if (onSuccessfulSubmit) {
              onSuccessfulSubmit();
            };
          },
          onError: () => notify.error("Failed to add a comment!"),
          onSettled: () => {
            queryClient.invalidateQueries([QUERY_KEYS.COMMENTS, billId]);
            queryClient.invalidateQueries([QUERY_KEYS.UTILITY_BILLS, billId]);
          },
        },
      )
      setValue("")
    };
  };

  return (
    <>
      {!parentId && siteId !== undefined && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            pt: 1,
          }}
        >
          <FormControlLabel
            sx={{ ml: 1 }}
            control={
              <Checkbox
                checked={isTask}
                onChange={() => setIsTask((v) => !v)}
              />
            }
            label="Mark as task"
          />
          <UserSelectPopover onUserSelect={onUserSelect} siteId={siteId} />
        </Box>
      )}
      <TextField
        fullWidth
        disabled={isCreating || isCreatingSite}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                disabled={!value || isCreating || isCreatingSite}
                aria-label="send"
                onClick={handleComments}
                edge="end"
              >
                <SendOutlined />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </>
  );
};
